import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import type { SortingRule } from "react-table";
import useSWR from "swr";
import { StringParam, useQueryParams } from "use-query-params";
import { FiltersArea } from "../../../../../../components/Filters/Filters";
import { TablePageLayout } from "../../../../../../components/TablePage/TablePageLayout";
import type { TableRefProps } from "../../../../../../components/Table/utils";
import { TableWithBulkAction } from "../../../../../../components/Table/TableWithBulkAction";
import { endpoints } from "../../../../../../endpoints";
import type {
  OptionType,
  TagClassificationConfig,
} from "../../../../../../types/types";
import type {
  EntityProfilePaginatedOutput,
  EntityType,
} from "../../../../../../types/types.MDM";
import { useDebounce } from "../../../../../../util/hooks";
import {
  defaultHandleSort,
  DEFAULT_PER_PAGE,
  TablePlaceholder,
  updateCustomLabels,
  useStoreState,
  useSupportedLanguages,
} from "../../../../../../util/util";
import type { Filters } from "../../../../../../util/filterStateManager";
import {
  applyFiltersToURL,
  loadFilters,
} from "../../../../../../util/filterStateManager";
import { createChangePerPageHandler } from "../../../../../../components/Pagination/util";
import { getEntityEndpoint } from "../EntityListPage";
import type { EntityUploadState } from "./createEntityFromUploads.util";
import { DEFAULT_STATUSES } from "./createEntityFromUploads.util";

const PER_PAGE_ITEMS = [10, 20, 50];

type EntityListRow = {
  id: string;
  externalId: string;
  name: string;
};

const ENTITY_LIST_QUERY_PARAMS = {
  template: StringParam,
  q: StringParam,
};

/**
 * Row picker used by the "Specific" option of the entity template download step,
 * mirroring CreateProductsList for the products bulk-upload flow.
 */
export function CreateEntitiesList({
  entityType,
  handleSelectRows,
  selectedTemplate,
  defaultSelectedRows,
}: {
  entityType: EntityType;
  handleSelectRows: (rows: EntityUploadState["selected_entities"]) => void;
  selectedTemplate: OptionType<string>;
  defaultSelectedRows: EntityUploadState["selected_entities"];
}) {
  const [query, setQuery] = useQueryParams(ENTITY_LIST_QUERY_PARAMS);

  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "asc",
  });
  const [filtersFromUrl, setFiltersFromUrl] = useState<Filters>();
  const [tableData, setTableData] = useState<EntityListRow[]>([]);
  const [searchQuery, setSearchQuery] = useState(query.q || "");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 2000);
  const [offset, setOffset] = useState(0);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
    totalCount: 0,
  });
  const { t } = useTranslation();
  const { supportedLanguageOptions } = useSupportedLanguages();
  const { storefront_id, tenant_id } = useStoreState();
  const location = useLocation();
  const { pathname } = location;
  const history = useHistory();
  const resetSelectedRowsRef = useRef<TableRefProps>(null);

  const { data: customLabels, error: customLabelsError } = useSWR<
    TagClassificationConfig[]
  >(
    storefront_id
      ? endpoints.v2_storefronts_id_mdm_entity_type_filters_custom_labels(
          storefront_id,
          entityType
        )
      : null,
    { revalidateOnFocus: true }
  );

  updateCustomLabels(customLabels, supportedLanguageOptions);

  const handleSort = async (rules: SortingRule<object>[]) =>
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);

  const changePerPage = createChangePerPageHandler(
    offset,
    setPerPage,
    setOffset
  );

  const constructQuery = (query?: string) => {
    const paramsWithOffset = new URLSearchParams(
      `offset=${offset}&limit=${perPage}&template=${selectedTemplate.label}`
    );
    DEFAULT_STATUSES.forEach((status) =>
      paramsWithOffset.append("status", status)
    );
    if (query) paramsWithOffset.append("q", query);
    paramsWithOffset.append("order_by", sortingRules.orderBy || "asc");
    if (sortingRules.sortBy) {
      paramsWithOffset.append("sort_by", sortingRules.sortBy);
    }
    return (
      getEntityEndpoint(entityType, tenant_id) +
      "?" +
      applyFiltersToURL({
        filtersFromUrl,
        params: paramsWithOffset,
      })
    );
  };

  const { data: entitiesResponse, error: entitiesError } =
    useSWR<EntityProfilePaginatedOutput>(constructQuery(debouncedSearchQuery));

  const isLoading = !entitiesResponse && !entitiesError;

  const tableColumns = useMemo(
    () => [
      {
        header: t("Entity ID"),
        accessorKey: "externalId",
        id: "externalId",
        enableSorting: false,
      },
      {
        header: t("Company Name"),
        accessorKey: "name",
        id: "name",
      },
    ],
    [t]
  );

  const handleClearFilter = () => {
    history.replace(pathname);
    setQuery({ q: query.q, template: selectedTemplate.label });
    setOffset(0);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOffset(0);
    setSearchQuery(e.target.value);
  };
  const handleClearSearch = () => {
    setSearchQuery("");
    setQuery({ q: undefined });
    setOffset(0);
  };

  const changePage = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  const onResetRows = () => {
    resetSelectedRowsRef.current?.resetSelectedRows();
  };

  useEffect(() => {
    const filters = loadFilters(location);
    setFiltersFromUrl(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname]);

  useEffect(() => {
    const handleEntitiesData = ({
      data,
      pagination,
    }: EntityProfilePaginatedOutput) => {
      const tableData = data.map(({ id, name, entity_number }) => ({
        id,
        externalId: entity_number ?? "--",
        name: name ?? "--",
      }));
      let is_data_changed = false;
      setTableData((prev) => {
        is_data_changed =
          prev.length !== tableData.length ||
          prev.some((row, index) => row.id !== tableData[index]?.id);
        return tableData;
      });
      setTablePagination((prev) => {
        const cur = {
          perPage: perPage,
          pageCount: Math.ceil(pagination.total / perPage),
          pageIndex: pagination.offset / perPage + 1,
          totalCount: pagination.total,
        };
        if (is_data_changed && prev.pageIndex === cur.pageIndex) {
          onResetRows();
        }
        return cur;
      });
    };

    if (entitiesResponse) {
      const { data: entities, pagination } = entitiesResponse;
      handleEntitiesData({ data: entities, pagination });
    }
  }, [entitiesResponse, perPage]);

  useEffect(() => {
    setQuery({ template: selectedTemplate.label });
  }, [selectedTemplate.label, setQuery]);

  useEffect(() => {
    if (query.q && query.q !== searchQuery) {
      setSearchQuery(query.q);
    }
  }, [query.q, searchQuery]);

  return (
    <TablePageLayout
      containerStyle={{ marginTop: "30px" }}
      // Toolbar
      searchQuery={searchQuery}
      searchPlaceholder={t("Search by name or ID")}
      onSearchChange={handleSearch}
      onSearchClear={handleClearSearch}
      // Filters
      filtersSlot={
        !customLabelsError ? (
          <FiltersArea
            resetOffset={setOffset}
            customLabels={customLabels ?? []}
            clearFilter={handleClearFilter}
            type={entityType}
          />
        ) : undefined
      }
      // Meta row
      totalCount={tablePagination.totalCount}
      totalCountLabel={t("entities")}
      perPageItems={PER_PAGE_ITEMS}
      perPage={perPage}
      onPerPageChange={changePerPage}
      // Table
      tableSlot={
        <TableWithBulkAction
          columns={tableColumns}
          data={tableData}
          isLoading={isLoading}
          error={entitiesError}
          handleSort={handleSort}
          handleSelectRows={handleSelectRows}
          defaultSelectedRows={defaultSelectedRows}
          bulkActionElement={() => <></>}
          ref={resetSelectedRowsRef}
          bulkSelectLimit={500}
          Placeholder={<TablePlaceholder />}
        />
      }
      // Pagination
      pagination={tablePagination}
      offset={offset}
      onPageChange={changePage}
    />
  );
}
